import { getT } from '@/i18n';
import { useT } from '@/i18n/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBackHandler } from '@/hooks/useBackHandler';
import { api, ApiError, type WeatherPlaceHit } from '@/lib/api';
import type { WeatherPlaceChoice } from '@/lib/weatherPlace';
import './weatherCityPicker.css';

/**
 * Az időjárás helye — kereshető városlista (GRUNDO #61, Geri kérése).
 *
 * A widget alatti „Budapest, 15:42 ⓘ" sorra koppintva nyílik. A lista tetején
 * MINDIG ott a „Saját helyzetem" — ezzel lehet visszaállni a választott
 * városról. Keresés gépelés közben, rövid várakozással: nem minden
 * billentyűleütés megy ki.
 *
 * Teljes képernyős lap, ugyanaz a viselkedés, mint a többi (követők,
 * értesítések): fejléc balra, bezárás jobbra, Escape és az Android vissza
 * gomb zár, `z-index` a lapok szintjén (60).
 */

/** Ennyi szünet után indul a keresés — gépelés közben nem kérdezünk minden betűre. */
const SEARCH_DEBOUNCE_MS = 300;
/** A szerverrel egyező küszöb (`routes/weather.ts` → PLACES_MIN_QUERY). */
const MIN_QUERY = 2;

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'done'; items: WeatherPlaceHit[] }
  | { kind: 'error'; message: string };

export function WeatherCityPicker({
  current,
  onSelect,
  onClose,
}: {
  /** A mostani választás — `null` a saját helyzet. */
  current: WeatherPlaceChoice | null;
  onSelect: (choice: WeatherPlaceChoice | null) => void;
  onClose: () => void;
}) {
  const t = useT();
  useBackHandler(true, () => onClose());

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="wcity" role="dialog" aria-modal="true" aria-label={t.cityPicker.title}>
      <header className="wcity__head">
        <h2 className="wcity__title">{t.cityPicker.title}</h2>
        <button type="button" className="wcity__close" aria-label={t.cityPicker.close} onClick={onClose}>
          <CloseIcon />
        </button>
      </header>
      <CitySearchList current={current} onSelect={onSelect} autoFocus />
    </div>,
    document.body,
  );
}

/**
 * A kereső és a találati lista — a felugró lap (`WeatherCityPicker`) és a
 * Beállítások → Lokáció képernyő (`LocationScreen`) KÖZÖS része, hogy a két
 * helyen ugyanúgy működjön a városválasztás.
 *
 * @param inline a beállítási képernyőn a lista a lap görgetésével megy, nem
 *   saját görgetődobozban.
 */
export function CitySearchList({
  current,
  onSelect,
  autoFocus = false,
  inline = false,
}: {
  current: WeatherPlaceChoice | null;
  onSelect: (choice: WeatherPlaceChoice | null) => void;
  autoFocus?: boolean;
  inline?: boolean;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState<SearchState>({ kind: 'idle' });
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      setSearch({ kind: 'idle' });
      return;
    }
    let alive = true;
    setSearch({ kind: 'loading' });
    const timer = window.setTimeout(() => {
      api
        .weatherPlaces(trimmed)
        .then((result) => {
          if (alive) setSearch({ kind: 'done', items: result.items });
        })
        .catch((problem: unknown) => {
          if (!alive) return;
          setSearch({
            kind: 'error',
            message:
              problem instanceof ApiError
                ? problem.message
                : getT().cityPicker.failed,
          });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  return (
    <>
      <div className={`wcity__search${inline ? ' wcity__search--inline' : ''}`}>
        <SearchIcon />
        <input
          ref={input}
          className="wcity__input"
          /* `text`, nem `search`: a böngésző saját (kék) törlő-X-e nem követné a témát. */
          type="text"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          placeholder={t.cityPicker.placeholder}
          aria-label={t.cityPicker.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className={`wcity__list${inline ? ' wcity__list--inline' : ''}`}>
        <button
          type="button"
          className={`wcity__row${current === null ? ' wcity__row--current' : ''}`}
          aria-current={current === null ? 'true' : undefined}
          onClick={() => onSelect(null)}
        >
          <span className="wcity__icon" aria-hidden="true">
            <PinIcon />
          </span>
          <span className="wcity__text">
            <span className="wcity__name">{t.cityPicker.ownLocation}</span>
            <span className="wcity__region">Ahol most vagy</span>
          </span>
        </button>

        {current !== null && search.kind === 'idle' ? (
          /* A választott város a keresés előtt is látsszon — különben a
             felhasználó nem látná, mi van most beállítva. */
          <button type="button" className="wcity__row wcity__row--current" aria-current="true" onClick={() => onSelect(current)}>
            <span className="wcity__icon" aria-hidden="true">
              <CityIcon />
            </span>
            <span className="wcity__text">
              <span className="wcity__name">{current.name}</span>
              <span className="wcity__region">{t.cityPicker.selected}</span>
            </span>
          </button>
        ) : null}

        {search.kind === 'idle' ? (
          <p className="wcity__note">{t.cityPicker.minQuery(MIN_QUERY)}</p>
        ) : search.kind === 'loading' ? (
          <p className="wcity__note">{t.cityPicker.searching}</p>
        ) : search.kind === 'error' ? (
          <p className="wcity__note" role="alert">
            {search.message}
          </p>
        ) : search.items.length === 0 ? (
          <p className="wcity__note">{t.cityPicker.empty}</p>
        ) : (
          search.items.map((item) => {
            const selected =
              current !== null &&
              current.name === item.name &&
              current.lat.toFixed(2) === item.lat.toFixed(2) &&
              current.lon.toFixed(2) === item.lon.toFixed(2);
            return (
              <button
                key={`${item.name}|${item.lat}|${item.lon}`}
                type="button"
                className={`wcity__row${selected ? ' wcity__row--current' : ''}`}
                aria-current={selected ? 'true' : undefined}
                onClick={() => onSelect({ name: item.name, lat: item.lat, lon: item.lon })}
              >
                <span className="wcity__icon" aria-hidden="true">
                  <CityIcon />
                </span>
                <span className="wcity__text">
                  <span className="wcity__name">{item.name}</span>
                  {item.region ? <span className="wcity__region">{item.region}</span> : null}
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

const icon = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function CloseIcon() {
  return (
    <svg {...icon} strokeWidth={2.2}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg {...icon} className="wcity__search-icon">
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg {...icon}>
      <path d="M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  );
}

function CityIcon() {
  return (
    <svg {...icon}>
      <path d="M4 20V9l5-3v14M9 20V4l7 3v13M16 20v-8l4 2v6M3 20h18" />
    </svg>
  );
}
